import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@descope/react-sdk';
import { Global } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { PostHogProvider } from 'posthog-js/react';

import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { AvatarDisplayProvider } from '@/hooks/useAvatarDisplay';
import { CanduInit } from '@/hooks/useCandu';
import { DirectionProvider, useDirection } from '@/hooks/useDirection';
import { IndustryProvider } from '@/hooks/useIndustries';
import { OfficeProvider } from '@/hooks/useOffices';
import { TitleProvider } from '@/hooks/usePageTitle';
import { QuerySuspenderProvider } from '@/hooks/useQuerySuspender';
import { SnackbarProvider } from '@/hooks/useSnackbar';
import { CustomThemeProvider } from '@/theme';
import palette from '@/theme/palette';
import primereact from '@/theme/primereact';
import { combineProviders } from '@/utils/combineProviders';

import routes from './routes';

/** Static app config derived from environment variables. */
const FALLBACK_PROJECT_ID = 'P2gMiWceDSCZkxrEf85p2S1ssjx9';
const isDev = import.meta.env.MODE === 'development';
const projectId = import.meta.env.VITE_DESCOPE_PROJECT_ID || FALLBACK_PROJECT_ID;
const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24'
} as const;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: false,
      refetchOnWindowFocus: isDev ? false : true
    }
  }
});

const router = createBrowserRouter(routes);

const posthogProviderEntry: [typeof PostHogProvider, { options: typeof posthogOptions; apiKey: string }] = [
  PostHogProvider,
  { options: posthogOptions, apiKey: posthogApiKey }
];

const Providers = combineProviders([
  ErrorBoundary,
  TitleProvider,
  DirectionProvider,
  ...(isDev ? [] : [posthogProviderEntry]),
  [QueryClientProvider, { client: queryClient }],
  [AuthProvider, { projectId }],
  IndustryProvider,
  OfficeProvider,
  QuerySuspenderProvider,
  CustomThemeProvider,
  SnackbarProvider,
  AvatarDisplayProvider
]);

const AppContent = () => {
  const { direction } = useDirection();

  return (
    <>
      <CssBaseline />
      <CanduInit />
      <Global
        styles={{
          ':root, body': {
            fontSynthesis: 'none',
            textRendering: 'optimizeLegibility',
            MozOsxFontSmoothing: 'grayscale',
            width: '100%',
            height: '100%',
            minHeight: '100%',
            backgroundColor: palette.grey[50]
          },
          '#root': {
            height: '100%'
          },
          'body > .MuiModal-root': {
            direction: 'ltr'
          },
          'body > .MuiModal-root *': {
            direction
          }
        }}
      />
      <Global styles={primereact(palette)} />
      <RouterProvider router={router} />
    </>
  );
};

const App = () => (
  <Providers>
    <AppContent />
    <ReactQueryDevtools />
  </Providers>
);

export default App;
